import { GeometryApi } from '@cibo/core'
import { MapTheme } from '@cibo/mapgl'
import { useTheme } from '@mui/material'
import { useQueries } from '@tanstack/react-query'
import { useInventory } from 'features/Inventories'
import { Feature, Geometry } from 'geojson'
import { flatten } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { Layer, Source } from 'react-map-gl/dist/es5'
import { useParams, useSearchParams } from 'react-router-dom'

interface CurrentGeometriesLayerProps {
  currentFips?: string[]
  currentFeatures?: Feature<Geometry, { resourceId: string }>[]
}

const getFeature = (resourceId: string, key: string) =>
  GeometryApi.getGeometry(resourceId).then(geometry => ({
    type: 'Feature',
    properties: { resourceId: key },
    geometry,
  }))

export const CurrentGeometriesLayer = ({
  currentFeatures = [],
  currentFips: passedFips = [],
}: CurrentGeometriesLayerProps) => {
  const { inventoryId } = useParams()
  const theme = useTheme<MapTheme>()
  const [params] = useSearchParams()
  const type = params.get('type')
  const [inventoryFips, setInventoryFips] = useState<{ resourceId?: string; key: string }[]>([])
  const { data: inventory, dataUpdatedAt } = useInventory(inventoryId)
  const boundaryId = params.get('id')

  useEffect(() => {
    if (!inventory) return
    const fipsWithIds = flatten(
      inventory?.item.boundaries.map(boundary =>
        boundary.areaDef
          .map(area => ({ ...area, boundaryId: boundary.boundaryId }))
          .filter(area => area.type === 'fips')
          .map(area => ({ resourceId: area.fips, key: `${area.fips}-${area.boundaryId}` }))
      )
    )
    setInventoryFips(fipsWithIds)
  }, [dataUpdatedAt])

  const currentFips: { resourceId: string; key?: string }[] =
    !!boundaryId || type === 'draw' || type === 'select'
      ? passedFips.map(fip => ({ resourceId: fip }))
      : (inventoryFips as { resourceId: string; key?: string }[])

  const geometryQueries = useQueries({
    queries:
      currentFips?.map(({ resourceId, key }, index) => ({
        queryKey: ['supplyShedGeometry', key],
        queryFn: () => getFeature(resourceId, key || `${resourceId}-${index}`),
      })) || [],
  })

  const geometryFeatures = geometryQueries.map(({ data }) => data).filter(Boolean) as Feature<
    any,
    { resourceId: string }
  >[]

  const features = useMemo(
    () =>
      type === 'draw'
        ? currentFeatures
        : type === 'select'
        ? geometryFeatures
        : [...geometryFeatures, ...currentFeatures],
    [currentFeatures, currentFips, geometryFeatures, type]
  )

  return (
    <>
      {features.map(feature => (
        <Source type="geojson" data={feature} key={feature.properties.resourceId}>
          <Layer
            id={`selected-shed-shape-${feature.properties.resourceId}`}
            type="fill"
            paint={{
              'fill-color': theme.palette.selectFill,
              'fill-opacity': 0.3,
            }}
          />
          <Layer
            id={`selected-shed-stroke-${feature.properties.resourceId}`}
            type="line"
            paint={{
              'line-color': theme.palette.selectLine,
              'line-opacity': 0.5,
              'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 12, 7],
            }}
          />
          <Layer
            id={`selected-shed-base-${feature.properties.resourceId}`}
            type="line"
            paint={{
              'line-color': theme.palette.selectLine,
              'line-opacity': 0.5,
              'line-width': ['interpolate', ['linear'], ['zoom'], 10, 4, 12, 7],
            }}
          />
        </Source>
      ))}
    </>
  )
}
